/**
 * Runtime twin of main-frontend
 * `src/components/WebsiteBuilder/utils/fontSizeScale.ts` — keep behavior
 * identical.
 */
import type { BodyTheme, HeadingsTheme } from "../types/theme";

// Tailwind v4 default text sizes in px (text-md is a project no-op ≈ 16px).
export const TAILWIND_SIZE_PX: Record<string, number> = {
  "text-xs": 12,
  "text-sm": 14,
  "text-md": 16,
  "text-base": 16,
  "text-lg": 18,
  "text-xl": 20,
  "text-2xl": 24,
  "text-3xl": 30,
  "text-4xl": 36,
  "text-5xl": 48,
  "text-6xl": 60,
  "text-7xl": 72,
  "text-8xl": 96,
  "text-9xl": 128,
};

export const parsePx = (
  value: string | null | undefined,
): number | undefined => {
  if (typeof value !== "string") return undefined;
  const match = value.trim().match(/^(\d+(?:\.\d+)?)px$/);
  const px = match?.[1];
  return px !== undefined ? parseFloat(px) : undefined;
};

// Neutral "scale = 1" baseline per level. Matches the editor's
// BASE_THEME_DEFAULTS / the runtime DEFAULT_*_THEME sizes (36/22/16).
const REF_PX = { h1: 36, h2: 22, body: 16 } as const;

/**
 * Resolve the theme-SCALED font size (px string) for a Text node — twin of the
 * editor's resolveScaledFontSizePx. The node's Tailwind token (or absolute px)
 * is the relative base; the theme's per-level px control is a proportional
 * scale: finalPx = basePx × (themeLevelPx / REF_PX[level]). At the neutral
 * theme value the scale is exactly 1.0.
 *
 * @returns a `"NNpx"` string, or `null` when the node's size token is unknown.
 */
export const resolveScaledFontSizePx = ({
  nodeSize,
  level,
  headingsTheme,
  bodyTheme,
}: {
  nodeSize?: string | undefined;
  level?: "h1" | "h2" | undefined;
  headingsTheme?: HeadingsTheme | undefined;
  bodyTheme?: BodyTheme | undefined;
}): string | null => {
  // Base px from the node's Tailwind token, or a px value if already absolute.
  const basePx = nodeSize
    ? (TAILWIND_SIZE_PX[nodeSize] ?? parsePx(nodeSize))
    : undefined;
  if (basePx === undefined) return null;

  const refKey = level ?? "body";
  const targetPx =
    parsePx(level ? headingsTheme?.[level]?.fontSize : bodyTheme?.fontSize) ??
    REF_PX[refKey];

  const scale = targetPx / REF_PX[refKey];
  return `${Math.round(basePx * scale)}px`;
};

/**
 * Fluid display type: turn a fixed px size into a viewport-responsive
 * `clamp(round(px*0.5)px, (px/12.8)vw + 8px, pxpx)`. `fluid` falsy returns
 * the plain `"NNpx"` string — identical to the fixed-px behavior.
 */
export const resolveFluidFontSize = (px: number, fluid?: boolean): string => {
  if (!fluid) return `${px}px`;
  const minPx = Math.round(px * 0.5);
  const vw = +(px / 12.8).toFixed(3);
  return `clamp(${minPx}px, ${vw}vw + 8px, ${px}px)`;
};
